import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, GoogleAuthProvider,
  signInWithPopup, onAuthStateChanged, sendPasswordResetEmail,
} from "firebase/auth";
import { getDoc, setDoc, deleteDoc, doc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { AppDispatch } from "./store";

/** Firestore collection that holds the user profiles (doc id = uid). Change it here only. */
const DB_COLLECTION = "users";

/* ─────────────────────────────────────────────────────────────
   No API endpoints are called from this slice. Everything is the
   Firebase SDK talking to Auth + Firestore (owners/{uid}).
   Data thunks: upsertUserData · listenToUserData · deleteUserData
   Session thunks (needed to sign in at all): register / login /
   loginWithGoogle / logout / sendResetEmail / listenForAuthChanges
   ───────────────────────────────────────────────────────────── */

export interface ShippingAddress {
  streetAddress?: string;
  apartmentNumber?: string;
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  zipCode?: string;
}

export interface UserData {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  profilePicture?: string;
  joinDate: string;
  provider?: "password" | "google";
  shippingAddress?: ShippingAddress;
  [key: string]: any;
}

interface AuthState {
  user: UserData | null;
  status: "idle" | "loading" | "failed";
  error: string | null;
  authLoaded: boolean;
  isOwner: boolean;
}

const initialState: AuthState = { user: null, status: "idle", error: null, authLoaded: false, isOwner: false };

const mapAuthError = (err: any): string => {
  switch (err?.code) {
    case "auth/email-already-in-use": return "This email is already registered. Please log in instead.";
    case "auth/invalid-email": return "Please enter a valid email address.";
    case "auth/weak-password": return "Please use at least 6 characters.";
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found": return "Incorrect email or password.";
    case "auth/too-many-requests": return "Too many attempts. Please wait a moment and try again.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request": return "Sign-in was cancelled.";
    case "auth/account-exists-with-different-credential": return "This email is registered with a different sign-in method.";
    default: return err?.message || "Something went wrong. Please try again.";
  }
};

const readUser = async (uid: string): Promise<UserData | null> => {
  const snap = await getDoc(doc(db, DB_COLLECTION, uid));
  return snap.exists() ? ({ ...(snap.data() as UserData), userId: uid }) : null;
};

/* ───────────── DATA THUNKS ───────────── */

/** Creates owners/{uid} or merges fields into it. */
export const upsertUserData = createAsyncThunk(
  "auth/upsertUserData",
  async (data: Partial<UserData>, { rejectWithValue }) => {
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not logged in");
      const ref = doc(db, DB_COLLECTION, u.uid);
      const { isOwner: _ignored, ...safe } = data as any;
      await setDoc(ref, { ...safe, userId: u.uid, updatedAt: serverTimestamp() }, { merge: true });
      return (await readUser(u.uid)) as UserData;
    } catch (err: any) { return rejectWithValue(mapAuthError(err)); }
  }
);

/** Live listener on owners/{uid}. Returns the unsubscribe function. */
export const listenToUserData = (userId?: string) => (dispatch: AppDispatch) => {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) return () => {};
  return onSnapshot(
    doc(db, DB_COLLECTION, uid),
    (snap) => { if (snap.exists()) dispatch(setUser({ ...(snap.data() as UserData), userId: uid })); },
    (e) => console.error("[listenToUserData]", e)
  );
};

/** Deletes owners/{uid} and the Firebase Auth account. */
export const deleteUserData = createAsyncThunk("auth/deleteUserData", async (_, { rejectWithValue }) => {
  try {
    const u = auth.currentUser;
    if (!u) throw new Error("Not logged in");
    await deleteDoc(doc(db, DB_COLLECTION, u.uid));
    await u.delete();
    return u.uid;
  } catch (err: any) { return rejectWithValue(mapAuthError(err)); }
});

/** True when owners/{uid} exists (shop owner). */
export const checkIsOwner = createAsyncThunk("auth/checkIsOwner", async () => {
  const u = auth.currentUser;
  if (!u) return false;
  try { return (await getDoc(doc(db, DB_COLLECTION, u.uid))).get("isOwner") === true; } catch { return false; }
});

/* ───────────── SESSION THUNKS ───────────── */

export const registerWithEmail = createAsyncThunk(
  "auth/registerWithEmail",
  async (p: { email: string; password: string; firstName: string; lastName: string }, { rejectWithValue }) => {
    try {
      const email = p.email.trim().toLowerCase();
      const cred = await createUserWithEmailAndPassword(auth, email, p.password);
      const userDoc: UserData = {
        userId: cred.user.uid, email, firstName: p.firstName.trim(), lastName: p.lastName.trim(),
        joinDate: new Date().toISOString(), provider: "password",
      };
      await setDoc(doc(db, DB_COLLECTION, cred.user.uid), userDoc);
      return userDoc;
    } catch (err: any) { return rejectWithValue(mapAuthError(err)); }
  }
);

export const loginWithEmail = createAsyncThunk(
  "auth/loginWithEmail",
  async (p: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, p.email.trim(), p.password);
      const user = await readUser(cred.user.uid);
      if (!user) throw new Error("Account data not found. Please contact support.");
      return user;
    } catch (err: any) { return rejectWithValue(mapAuthError(err)); }
  }
);

export const loginWithGoogle = createAsyncThunk("auth/loginWithGoogle", async (_, { rejectWithValue }) => {
  try {
    const { user: u } = await signInWithPopup(auth, new GoogleAuthProvider());
    let user = await readUser(u.uid);
    if (!user) {
      const [first = "", ...rest] = (u.displayName || "").trim().split(" ");
      const userDoc: UserData = {
        userId: u.uid, email: (u.email || "").toLowerCase(), firstName: first, lastName: rest.join(" "),
        profilePicture: u.photoURL || "", joinDate: new Date().toISOString(), provider: "google",
      };
      await setDoc(doc(db, DB_COLLECTION, u.uid), userDoc);
      user = userDoc;
    }
    return user;
  } catch (err: any) { return rejectWithValue(mapAuthError(err)); }
});

export const logout = createAsyncThunk("auth/logout", async () => { await signOut(auth); });

export const sendResetEmail = createAsyncThunk("auth/sendResetEmail", async (email: string) => {
  await sendPasswordResetEmail(auth, email);
});

/** Subscribes to Firebase Auth state. Returns the unsubscribe function. */
export const listenForAuthChanges = () => (dispatch: AppDispatch) =>
  onAuthStateChanged(auth, async (fb) => {
    if (!fb) { dispatch(setUser(null)); dispatch(setAuthLoaded()); return; }
    try { dispatch(setUser(await readUser(fb.uid))); } catch { dispatch(setUser(null)); }
    dispatch(setAuthLoaded());
  });

/* ───────────── SLICE ───────────── */

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (s, a: PayloadAction<UserData | null>) => { s.user = a.payload; },
    setAuthLoaded: (s) => { s.authLoaded = true; },
    clearAuthError: (s) => { s.error = null; s.status = "idle"; },
  },
  extraReducers: (b) => {
    const ok = (s: AuthState, a: PayloadAction<UserData>) => { s.user = a.payload; s.status = "idle"; s.error = null; };
    const busy = (s: AuthState) => { s.status = "loading"; s.error = null; };
    const bad = (s: AuthState, a: any) => { s.status = "failed"; s.error = (a.payload as string) || a.error?.message || "Failed"; };
    b.addCase(registerWithEmail.pending, busy).addCase(registerWithEmail.fulfilled, ok).addCase(registerWithEmail.rejected, bad)
      .addCase(loginWithEmail.pending, busy).addCase(loginWithEmail.fulfilled, ok).addCase(loginWithEmail.rejected, bad)
      .addCase(loginWithGoogle.pending, busy).addCase(loginWithGoogle.fulfilled, ok).addCase(loginWithGoogle.rejected, bad)
      .addCase(upsertUserData.fulfilled, ok).addCase(upsertUserData.rejected, bad)
      .addCase(deleteUserData.fulfilled, (s) => { s.user = null; s.isOwner = false; }).addCase(deleteUserData.rejected, bad)
      .addCase(logout.fulfilled, (s) => { s.user = null; s.isOwner = false; })
      .addCase(checkIsOwner.fulfilled, (s, a: PayloadAction<boolean>) => { s.isOwner = a.payload; });
  },
});

export const { setUser, setAuthLoaded, clearAuthError } = authSlice.actions;
export default authSlice.reducer;